import { useState, useEffect, useMemo } from "react";
import { Plus, MagnifyingGlass, ArrowsLeftRight, DeviceMobile, User, X, Trash, Receipt as ReceiptIcon, Eye, Wrench } from "@phosphor-icons/react";
import { api, formatApiError, TRADE_IN_PAYOUT_LABELS } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import Layout from "../components/Layout";
import TradeInIntakeModal from "../components/TradeInIntakeModal";
import TradeInDetailDrawer from "../components/TradeInDetailDrawer";

/**
 * Trade-In Inventory page — buy used devices from customers, then resell at
 * POS using the auto-generated `TI-XXXXX` SKU.
 *
 * Workflow:
 *   1. Click "Buy Device" → opens TradeInIntakeModal (customer picker + device
 *      details + buy/sell pricing + payout method).
 *   2. The intake creates a `Product` in inventory under the "Trade-In" category
 *      with SKU = trade-in number, so cashiers can ring it up at POS by typing
 *      the TI-XXXXX number into the existing pickup/settle search box.
 *   3. When sold at POS, the trade-in flips to status=sold and the linked sale
 *      is recorded for P&L reporting.
 */
const STATUS_TONE = {
  in_stock: { bg: "#EBF5FF", fg: "#1849C8", label: "In stock" },
  sold:     { bg: "#E6F4EE", fg: "#1F6B4F", label: "Sold" },
  returned: { bg: "#F3F4F6", fg: "#52525B", label: "Returned" },
  voided:   { bg: "#FEF2F2", fg: "#B91C1C", label: "Voided" },
};

const FILTER_TABS = [
  { key: "all", label: "All" },
  { key: "in_stock", label: "In stock" },
  { key: "sold", label: "Sold" },
  { key: "voided", label: "Voided" },
];

const fmtMoney = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return String(iso).slice(0, 10); }
};

export default function TradeIns() {
  const { currentStore } = useStore();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [report, setReport] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [healing, setHealing] = useState(false);
  const [healReport, setHealReport] = useState(null);
  // Side drawer for viewing & editing a single trade-in. Null = closed;
  // set to a TI id to open. Triggered by the row click (anywhere outside
  // the action icons) OR the dedicated eye icon in the Actions column.
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    try {
      const params = { limit: 200 };
      if (filter !== "all") params.status = filter;
      if (q.trim()) params.q = q.trim();
      const [list, rep] = await Promise.all([
        api.get("/trade-ins", { params }).then((r) => r.data),
        api.get("/reports/trade-ins").then((r) => r.data),
      ]);
      setItems(list.items || []);
      setCounts(list.counts || {});
      setReport(rep);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // ── Heal orphaned trade-ins ────────────────────────────────────────
  // A trade-in is "orphaned" when its status is `sold` but the linked
  // sale no longer contains it (customer returned/exchanged, invoice was
  // edited to remove the line, or the sale was deleted). Prior to the
  // sale-edit reconciliation fix, these units froze as `sold` and
  // couldn't be resold. This admin action walks every `sold` trade-in at
  // the current store and flips genuine orphans back to `in_stock`.
  const runHeal = async () => {
    if (healing) return;
    const pin = window.prompt(
      "Fix stuck 'Sold' trade-ins?\n\n" +
      "This scans every device flagged as SOLD and, if the linked sale " +
      "no longer contains it (customer exchange / removed line / voided " +
      "sale), flips it back to IN STOCK so it can be resold.\n\n" +
      "Enter admin PIN to continue:"
    );
    if (!pin) return;
    setHealing(true);
    setHealReport(null);
    try {
      const { data } = await api.post(
        "/trade-ins/heal-orphans",
        {},
        { headers: { "X-Admin-Pin": pin.trim() } },
      );
      setHealReport(data);
      if (data.healed === 0) {
        toast.success(`Scanned ${data.checked} sold trade-ins — none stuck. 🎉`);
      } else {
        toast.success(`Fixed ${data.healed} stuck trade-in${data.healed === 1 ? "" : "s"} out of ${data.checked} checked.`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Heal failed");
    } finally {
      setHealing(false);
    }
  };

  useEffect(() => {
    if (!currentStore?.id) return;
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filter, q, currentStore?.id]);

  const allCount = useMemo(() => Object.values(counts).reduce((a, b) => a + b, 0), [counts]);

  const onVoid = async (ti) => {
    if (!window.confirm(`Void ${ti.trade_in_number}?\n\nThis removes the product from inventory and reclaims any store credit issued.`)) return;
    try {
      await api.delete(`/trade-ins/${ti.id}`);
      toast.success(`${ti.trade_in_number} voided`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Layout>
      <div className="min-h-full bg-[#FAFAFA] pb-12" data-testid="trade-ins-page">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB]">
        <div className="px-6 py-5 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-[22px] font-semibold tracking-tight flex items-center gap-2">
              <ArrowsLeftRight size={22} weight="duotone" className="text-[#1E5FEB]" />
              Trade-In Inventory
            </h1>
            <div className="text-[12px] text-[#6B7280] mt-0.5">
              Buy used devices from customers and resell them at POS with a TI-XXXXX number.
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={runHeal}
                disabled={healing}
                title="Scan for trade-ins stuck as 'sold' after an invoice edit / customer exchange and reset them to in-stock."
                className="h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[#B45309] text-[12px] font-semibold hover:border-[#B45309] hover:bg-[#FEF3C7] inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="trade-in-heal-orphans-btn"
              >
                <Wrench size={13} weight="bold" />
                {healing ? "Scanning…" : "Fix stuck ‘Sold’"}
              </button>
            )}
            <button
              onClick={() => setIntakeOpen(true)}
              className="h-10 px-4 rounded-lg bg-[#1E5FEB] text-white text-[13px] font-semibold hover:bg-[#1849C8] inline-flex items-center gap-1.5 shadow-sm"
              data-testid="trade-in-buy-device-btn"
            >
              <Plus size={14} weight="bold" />
              Buy Device
            </button>
          </div>
        </div>

        {/* Heal result banner — visible after admin runs the fix. */}
        {healReport && (
          <div
            className={`mx-6 mt-3 rounded-xl border px-4 py-3 text-[12px] ${
              healReport.healed > 0
                ? "border-[#FDE68A] bg-[#FEF9C3] text-[#78350F]"
                : "border-[#D1FAE5] bg-[#ECFDF5] text-[#065F46]"
            }`}
            data-testid="trade-in-heal-report"
          >
            <div className="flex items-baseline justify-between">
              <div>
                <span className="font-semibold">
                  {healReport.healed > 0
                    ? `Restored ${healReport.healed} stuck trade-in${healReport.healed === 1 ? "" : "s"}`
                    : "All sold trade-ins are healthy"}
                </span>
                <span className="ml-2 opacity-70">· scanned {healReport.checked}</span>
              </div>
              <button
                onClick={() => setHealReport(null)}
                className="opacity-60 hover:opacity-100"
                aria-label="Dismiss"
              >
                <X size={12} weight="bold" />
              </button>
            </div>
            {healReport.details?.length > 0 && (
              <ul className="mt-2 space-y-0.5 font-mono text-[11px]">
                {healReport.details.map((d) => (
                  <li key={d.trade_in_id}>
                    <span className="font-semibold">{d.trade_in_number || d.trade_in_id.slice(0, 8)}</span>
                    {" · "}
                    <span className="opacity-70">{d.reason}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* P&L stat strip */}
        {report && (
          <div className="px-6 pb-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Bought" value={fmtMoney(report.bought_total)} tone="#1E5FEB" testid="ti-stat-bought" />
            <Stat label="Sold" value={fmtMoney(report.sold_total)} tone="#1F6B4F" testid="ti-stat-sold" />
            <Stat label="Margin" value={fmtMoney(report.margin)} tone={report.margin >= 0 ? "#1F6B4F" : "#B91C1C"} testid="ti-stat-margin" />
            <Stat label="Stock value (at buy)" value={fmtMoney(report.stock_value_at_buy_price)} tone="#52525B" testid="ti-stat-stock" />
          </div>
        )}
      </div>

      {/* Filters + search */}
      <div className="px-6 mt-4 flex items-center gap-2 flex-wrap">
        {FILTER_TABS.map((t) => {
          const c = t.key === "all" ? allCount : (counts[t.key] || 0);
          const active = filter === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`h-9 px-3.5 rounded-full text-[12px] font-medium transition-colors ${
                active ? "bg-[#121216] text-white" : "bg-white text-[#52525B] border border-[#E5E7EB] hover:border-[#1E5FEB]"
              }`}
              data-testid={`ti-filter-${t.key}`}
            >
              {t.label} <span className="ml-1.5 opacity-60">{c}</span>
            </button>
          );
        })}
        <div className="ml-auto relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search TI#, customer, IMEI…"
            className="h-9 pl-8 pr-3 rounded-lg border border-[#E5E7EB] text-[13px] w-64 focus:border-[#1E5FEB] outline-none"
            data-testid="ti-search-input"
          />
        </div>
      </div>

      {/* Table */}
      <div className="px-6 mt-4">
        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-[#FAFAFA] border-b border-[#E5E7EB]">
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
                  <Th>TI #</Th>
                  <Th>Date</Th>
                  <Th>Customer</Th>
                  <Th>Device</Th>
                  <Th className="text-right">Buy</Th>
                  <Th className="text-right">Sell</Th>
                  <Th>Payout</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-12 text-[#9CA3AF]">
                    {filter === "all" ? "No trade-ins yet — click \"Buy Device\" to add one." : "No trade-ins match this filter."}
                  </td></tr>
                )}
                {items.map((ti) => {
                  const tone = STATUS_TONE[ti.status] || STATUS_TONE.in_stock;
                  return (
                    <tr
                      key={ti.id}
                      className="border-b border-[#F3F4F6] last:border-0 hover:bg-[#FAFAFA] cursor-pointer"
                      onClick={() => setDetailId(ti.id)}
                      data-testid={`ti-row-${ti.trade_in_number}`}
                    >
                      <Td>
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono font-semibold text-[#1849C8]">{ti.trade_in_number}</span>
                          {ti.batch_id && ti.batch_size > 1 && (
                            <span
                              className="text-[9px] font-bold tracking-[0.08em] uppercase px-1.5 py-0.5 rounded-full bg-[#EFF6FF] text-[#1E40AF] border border-[#BFDBFE]"
                              title={`Part of a multi-device intake of ${ti.batch_size} devices`}
                              data-testid={`ti-batch-pill-${ti.trade_in_number}`}
                            >
                              +{ti.batch_size - 1} more
                            </span>
                          )}
                        </div>
                      </Td>
                      <Td className="text-[#52525B]">{fmtDate(ti.created_at)}</Td>
                      <Td>
                        <div className="font-medium">{ti.customer_name_snapshot || "—"}</div>
                        <div className="text-[11px] text-[#9CA3AF]">{ti.customer_phone_snapshot}</div>
                        {ti.intake_store_name && (
                          <div className="text-[10px] text-[#1E5FEB] uppercase tracking-wider font-semibold mt-0.5">
                            Bought at: {ti.intake_store_name.replace(/^iRepair Experts:\s*/i, "")}
                          </div>
                        )}
                      </Td>
                      <Td>
                        <div>{[ti.device_make, ti.device_model, ti.storage].filter(Boolean).join(" ") || "—"}</div>
                        {ti.imei && <div className="text-[11px] text-[#9CA3AF] font-mono">IMEI {ti.imei}</div>}
                      </Td>
                      <Td className="text-right tabular-nums">{fmtMoney(ti.buy_price)}</Td>
                      <Td className="text-right tabular-nums font-medium">{fmtMoney(ti.sell_price)}</Td>
                      <Td>
                        <span className="text-[11px] uppercase tracking-wider font-medium text-[#6B7280]">
                          {ti.payout_method === "store_credit" ? "Credit" : TRADE_IN_PAYOUT_LABELS[ti.payout_method] || "Cash"}
                        </span>
                        {ti.payout_reference && (
                          <div className="text-[10.5px] text-[#9CA3AF] font-mono truncate max-w-[140px]" title={ti.payout_reference} data-testid={`ti-payout-ref-${ti.trade_in_number}`}>
                            Ref {ti.payout_reference}
                          </div>
                        )}
                      </Td>
                      <Td>
                        <span className="px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider" style={{ background: tone.bg, color: tone.fg }}>
                          {tone.label}
                        </span>
                      </Td>
                      <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => setDetailId(ti.id)}
                            className="text-[#6B7280] hover:text-[#1E5FEB] p-1"
                            title="View / edit"
                            data-testid={`ti-view-${ti.trade_in_number}`}
                          >
                            <Eye size={14} weight="bold" />
                          </button>
                          {ti.status === "in_stock" && (
                            <button
                              onClick={() => onVoid(ti)}
                              className="text-[#9CA3AF] hover:text-[#B91C1C] p-1"
                              title="Void trade-in"
                              data-testid={`ti-void-${ti.trade_in_number}`}
                            >
                              <Trash size={14} weight="bold" />
                            </button>
                          )}
                          {ti.status === "sold" && ti.linked_sale_id && (
                            <a
                              href={`/invoices/${ti.linked_sale_id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-[#1E5FEB] hover:underline text-[11px] font-medium inline-flex items-center gap-1 ml-1"
                            >
                              <ReceiptIcon size={12} weight="bold" />
                              View sale
                            </a>
                          )}
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {intakeOpen && (
        <TradeInIntakeModal
          onClose={() => setIntakeOpen(false)}
          onCreated={() => { setIntakeOpen(false); load(); }}
        />
      )}

      {detailId && (
        <TradeInDetailDrawer
          tradeInId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={load}
        />
      )}
      </div>
    </Layout>
  );
}

function Stat({ label, value, tone, testid }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg px-4 py-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold">{label}</div>
      <div className="text-[18px] font-display font-semibold tabular-nums mt-0.5" style={{ color: tone }}>{value}</div>
    </div>
  );
}

function Th({ children, className = "" }) { return <th className={`px-4 py-3 ${className}`}>{children}</th>; }
function Td({ children, className = "", onClick }) { return <td className={`px-4 py-3 ${className}`} onClick={onClick}>{children}</td>; }
